import { useState, useEffect, useRef } from "react";
import gsap from "gsap";

interface ExplodeState {
  /** 폭발 진행률 (0 = 닫힘, 1 = 완전 열림) */
  progress: number;
  /** 폭발 완료 여부 */
  isExploded: boolean;
  /** 애니메이션 진행 중 여부 */
  isAnimating: boolean;
}

interface UseBuildingExplodeOptions {
  /** 폭발 애니메이션 시간 (초) */
  explodeDuration?: number;
  /** 복귀 애니메이션 시간 (초) */
  collapseDuration?: number;
  /** 폭발 완료 콜백 */
  onExplodeComplete?: () => void;
  /** 복귀 완료 콜백 */
  onCollapseComplete?: () => void;
}

/**
 * 건물 폭발 애니메이션 상태 관리 훅
 *
 * @param isSelected 건물 선택 상태
 * @param options 애니메이션 옵션
 * @returns 폭발 상태 및 진행률
 */
export function useBuildingExplode(
  isSelected: boolean,
  options: UseBuildingExplodeOptions = {}
): ExplodeState {
  const {
    explodeDuration = 0.6,
    collapseDuration = 0.4,
    onExplodeComplete,
    onCollapseComplete,
  } = options;

  const [state, setState] = useState<ExplodeState>({
    progress: 0,
    isExploded: false,
    isAnimating: false,
  });

  const tweenRef = useRef<gsap.core.Tween | null>(null);
  const progressRef = useRef({ value: 0 });

  // 선택 상태 변경 시 애니메이션
  useEffect(() => {
    // 기존 애니메이션 정리
    if (tweenRef.current) {
      tweenRef.current.kill();
    }

    if (isSelected) {
      // 폭발 애니메이션 시작
      setState((prev) => ({ ...prev, isAnimating: true }));

      tweenRef.current = gsap.to(progressRef.current, {
        value: 1,
        duration: explodeDuration,
        ease: "power2.out",
        onUpdate: () => {
          setState((prev) => ({
            ...prev,
            progress: progressRef.current.value,
          }));
        },
        onComplete: () => {
          setState({
            progress: 1,
            isExploded: true,
            isAnimating: false,
          });
          onExplodeComplete?.();
        },
      });
    } else if (progressRef.current.value > 0) {
      // 복귀 애니메이션 시작
      setState((prev) => ({ ...prev, isAnimating: true }));

      tweenRef.current = gsap.to(progressRef.current, {
        value: 0,
        duration: collapseDuration,
        ease: "power2.in",
        onUpdate: () => {
          setState((prev) => ({
            ...prev,
            progress: progressRef.current.value,
          }));
        },
        onComplete: () => {
          setState({
            progress: 0,
            isExploded: false,
            isAnimating: false,
          });
          onCollapseComplete?.();
        },
      });
    }

    return () => {
      if (tweenRef.current) {
        tweenRef.current.kill();
      }
    };
  }, [
    isSelected,
    explodeDuration,
    collapseDuration,
    onExplodeComplete,
    onCollapseComplete,
  ]);

  return state;
}

/**
 * 벽면별 폭발 오프셋 계산
 *
 * @param progress 폭발 진행률 (0~1)
 * @param _buildingSize 건물 크기 (바운딩 박스) - 더 이상 사용하지 않음
 * @param absoluteDistance 고정 이동 거리 (기본값: 8)
 */
export function calculateWallOffsets(
  progress: number,
  _buildingSize: { width: number; depth: number; height: number },
  absoluteDistance: number = 8.0
): {
  frontZ: number; // Z+ 정면
  sideX: number; // X+ 측면
  wallOpacity: number; // 벽면 투명도 (1 -> 0.1)
} {
  // 고정된 절대 거리로 이동 (건물 크기와 무관)
  const offset = absoluteDistance * progress;

  // 투명도: 1 -> 0.1 (progress 0 -> 1)
  const wallOpacity = 1 - progress * 0.9;

  return {
    frontZ: offset,
    sideX: offset,
    wallOpacity,
  };
}
